import { Box, Divider, SimpleGrid, Skeleton, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import {
  ExternalCustomer,
  ExternalDeal,
  useCheckExistingAccountHasPasswordLazyQuery,
  useUserDealsLazyQuery,
} from '../../gql/generated/graphql';
import { sameEmail } from '../../utils/customer';
import Modal from '../Modal';
import SavePasswordForm from '../SavePasswordForm';
import Label from '../formComponents/Label';
import ChangePassword from './ChangePassword';

export const PROFILE = 'profile';
export const PASSWORD = 'password';

type Props = {
  open: boolean;
  setOpen: (val: boolean) => void;
  customer?: ExternalCustomer;
};

const ProfileModal = ({ open, setOpen, customer }: Props) => {
  const [deals, setDeals] = useState<ExternalDeal[]>([]);
  const [getUserDeals, { loading: dealsLoading }] = useUserDealsLazyQuery({
    onCompleted: (data) => {
      setDeals(data?.userDeals as ExternalDeal[]);
    },
  });

  const [getHasPassword, { data: hasPassword }] = useCheckExistingAccountHasPasswordLazyQuery({
    fetchPolicy: 'network-only',
  });

  const sortedDeals = [...deals].sort((a, b) => Number(a?.id ?? 0) - Number(b?.id ?? 0));
  const lastDeals = sortedDeals?.slice(-2) ?? [];

  const shareEmail =
    lastDeals.length > 1 &&
    lastDeals[0]?.id === lastDeals[1]?.id &&
    (lastDeals[0]?.isCobuyer || lastDeals[1]?.isCobuyer) &&
    sameEmail(lastDeals[0]?.customer, lastDeals[1]?.cobuyer);

  const buyerDeal = lastDeals.find((d) => !d.isCobuyer);

  const loading = dealsLoading;

  useEffect(() => {
    if (!open) return;

    getUserDeals();
    getHasPassword();
  }, [open]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Modal onClose={handleClose} isOpen={open} size="2xl">
      {customer && (
        <>
          <Text fontSize="36px" fontWeight="bold" textAlign={{ base: 'center', md: 'left' }}>
            My Profile
          </Text>
          <SimpleGrid
            mt={customer?.full_name ? 6 : 2}
            columns={{ base: 1, md: 2 }}
            spacingX={2}
            spacingY={4}
            fontSize="16px"
          >
            <Box>
              <Skeleton isLoaded={!loading} height={loading ? '22px' : undefined}>
                <Label>Customer{shareEmail ? 's' : ''}:</Label>
                {shareEmail ? (
                  <Box color="black" fontWeight="semibold">
                    <Text>{buyerDeal?.customer?.full_name}</Text>
                    <Text>{buyerDeal?.cobuyer?.full_name}</Text>
                  </Box>
                ) : (
                  customer?.full_name && (
                    <>
                      <Text color="black" fontWeight="semibold">
                        {customer?.full_name}
                      </Text>
                    </>
                  )
                )}
              </Skeleton>
            </Box>

            <Box>
              <Skeleton isLoaded={!loading} height={loading ? '22px' : undefined}>
                {customer?.email && (
                  <>
                    <Label>Email Address:</Label>
                    <Text color="black" fontWeight="semibold">
                      {customer?.email}
                    </Text>
                  </>
                )}
              </Skeleton>
            </Box>
          </SimpleGrid>
          {hasPassword?.checkExistingAccountHasPassword != null && (
            <Divider borderColor="grayDarkBackground" my={4} />
          )}
        </>
      )}
      {hasPassword?.checkExistingAccountHasPassword && (
        <ChangePassword setOpen={setOpen} loading={loading} />
      )}
      {hasPassword?.checkExistingAccountHasPassword === false && (
        <Box mt={{ base: 2, md: 4 }}>
          <Text
            fontSize="18px"
            fontWeight="semibold"
            textAlign={{ base: 'center', md: 'left' }}
            mb={4}
          >
            Set a Password
          </Text>
          <SavePasswordForm onSuccess={getHasPassword} />
        </Box>
      )}
    </Modal>
  );
};

export default ProfileModal;
